
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { phpApi } from "@/integrations/php/client";
import {
  ArrowLeft, Calendar, Eye, LayoutDashboard, Mail, MapPin,
  MessageSquare, Phone, RefreshCw, User, Users, CheckCircle2,
  TrendingUp, Zap, Globe, Activity, Star, Clock, ChevronRight,
  MonitorSmartphone
} from "lucide-react";
import { format } from "date-fns";
import { el } from "date-fns/locale";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PartnerSMSMetrics from "@/components/partners/PartnerSMSMetrics";
import PartnerEmailMetrics from "@/components/partners/PartnerEmailMetrics";
import PartnerLeadsList from "@/components/partners/PartnerLeadsList";
import { ServiceType, LeadStageType } from "@/context/LeadContext";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";

interface Partner {
  id: string;
  name: string;
  phone: string;
  email: string;
  location: string;
  postalCode: string;
  services: string[];
  createdAt: string;
}

interface Lead {
  id: string;
  name: string;
  phone: string;
  address: string;
  postalCode: string;
  service: ServiceType;
  timeframe: string;
  comments?: string;
  stage: LeadStageType;
  partnerId: string | null;
  partnerName: string | null;
  createdAt: string;
  updatedAt: string;
  smsStatus: "sent" | "delivered" | "failed" | null;
  viewed: boolean;
  viewedAt: string | null;
  latitude: number | null;
  longitude: number | null;
}

interface PartnerMiniStats {
  views: number;
  callClicks: number;
  mapsClicks: number;
  interactions: number;
  interactionRate: number;
}

type TimeFilter = "all" | "week" | "month" | "quarter" | "year";

const TIME_FILTER_LABELS: Record<TimeFilter, string> = {
  all: "Όλο το διάστημα",
  week: "Εβδομάδα",
  month: "Μήνας",
  quarter: "Τρίμηνο",
  year: "Έτος",
};

const getServiceLabel = (service: string) => {
  const map: Record<string, string> = {
    εξοικονομώ: "Εξοικονομώ",
    αντλίεςΘερμότητας: "Αντλίες Θερμότητας",
    φωτοβολταϊκά: "Φωτοβολταϊκά",
    κλιματιστικά: "Κλιματιστικά",
    φυσικόΑέριο: "Φυσικό Αέριο",
    ηλιακά: "Ηλιακά",
    άλλο: "Άλλο",
  };
  return map[service] ?? service;
};

const getStageLabel = (stage: LeadStageType) => {
  const map: Record<string, string> = {
    Νέο: "Νέο",
    σεΕκκρεμότητα: "Σε Εκκρεμότητα",
    ήθελεΠληροφορίες: "Πληροφορίες",
    έκλεισεΗΔουλειά: "Έκλεισε",
  };
  return map[stage] ?? stage;
};

const getStageMeta = (stage: LeadStageType) => {
  const map: Record<string, { color: string; bg: string; dot: string }> = {
    Νέο:               { color: "text-violet-700", bg: "bg-violet-50 border-violet-200",  dot: "bg-violet-500" },
    σεΕκκρεμότητα:     { color: "text-amber-700",  bg: "bg-amber-50 border-amber-200",    dot: "bg-amber-500"  },
    ήθελεΠληροφορίες:  { color: "text-blue-700",   bg: "bg-blue-50 border-blue-200",      dot: "bg-blue-500"   },
    έκλεισεΗΔουλειά:   { color: "text-emerald-700",bg: "bg-emerald-50 border-emerald-200",dot: "bg-emerald-500"},
  };
  return map[stage] ?? { color: "text-slate-700", bg: "bg-slate-50 border-slate-200", dot: "bg-slate-400" };
};

const getInitials = (name: string) =>
  name.split(" ").map((p) => p[0]).join("").toUpperCase().slice(0, 2);

const PartnerLeadsDashboard = () => {
  const { partnerId } = useParams<{ partnerId: string }>();
  const [partner, setPartner] = useState<Partner | null>(null);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [miniStats, setMiniStats] = useState<PartnerMiniStats>({
    views: 0, callClicks: 0, mapsClicks: 0, interactions: 0, interactionRate: 0,
  });
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const { toast } = useToast();

  const calculateLeadScore = useCallback((lead: Lead) => {
    let score = 50;
    if (lead.timeframe === "άμεσα") score += 25;
    if (lead.timeframe === "εντός3μηνών") score += 12;
    if (lead.comments && lead.comments.trim().length > 15) score += 8;
    if (lead.viewed) score += 10;
    if (lead.stage === "Νέο") score += 10;
    if (lead.stage === "σεΕκκρεμότητα") score -= 5;
    return Math.max(0, Math.min(100, score));
  }, []);

  const fetchPartnerAndLeads = useCallback(async (showLoader = true) => {
    if (!partnerId) return;
    try {
      if (showLoader) setIsLoading(true);

      const { data: partnerData, error: partnerError } = await phpApi
        .from("partners").select("*").eq("id", partnerId).single();
      if (partnerError) throw new Error(partnerError.message);

      setPartner({
        id: partnerData.id,
        name: partnerData.name,
        phone: partnerData.phone,
        email: partnerData.email || "",
        location: partnerData.location,
        postalCode: partnerData.radius?.toString() || "",
        services: partnerData.services || [],
        createdAt: partnerData.created_at,
      });

      let leadsQuery = phpApi.from("leads").select("*").eq("partner_id", partnerId);
      const now = new Date();
      if (timeFilter === "week") {
        const d = new Date(now); d.setDate(d.getDate() - 7);
        leadsQuery = leadsQuery.gte("created_at", d.toISOString());
      } else if (timeFilter === "month") {
        const d = new Date(now); d.setMonth(d.getMonth() - 1);
        leadsQuery = leadsQuery.gte("created_at", d.toISOString());
      } else if (timeFilter === "quarter") {
        const d = new Date(now); d.setMonth(d.getMonth() - 3);
        leadsQuery = leadsQuery.gte("created_at", d.toISOString());
      } else if (timeFilter === "year") {
        const d = new Date(now); d.setFullYear(d.getFullYear() - 1);
        leadsQuery = leadsQuery.gte("created_at", d.toISOString());
      }

      const { data: leadsData, error: leadsError } = await leadsQuery.order("created_at", { ascending: false });
      if (leadsError) throw new Error(leadsError.message);

      setLeads(leadsData.map((lead: any) => ({
        id: lead.id,
        name: lead.name,
        phone: lead.phone,
        address: lead.address,
        postalCode: lead.postal_code || "",
        service: lead.service as ServiceType,
        timeframe: lead.timeframe,
        comments: lead.comments || undefined,
        stage: (lead.stage || "Νέο") as LeadStageType,
        partnerId: lead.partner_id || null,
        partnerName: null,
        createdAt: lead.created_at,
        updatedAt: lead.updated_at || lead.created_at,
        smsStatus: lead.sms_status || null,
        viewed: lead.link_opened || false,
        viewedAt: lead.viewed_at,
        latitude: lead.latitude || null,
        longitude: lead.longitude || null,
      })));

      setLastUpdated(new Date());

      try {
        const res = await fetch(`/api/get-partner-mini-stats.php?partnerId=${encodeURIComponent(partnerId)}`);
        if (res.ok) {
          const payload = await res.json();
          const first = Array.isArray(payload?.items) ? payload.items[0] : null;
          setMiniStats({
            views: Number(first?.views || 0),
            callClicks: Number(first?.callClicks || 0),
            mapsClicks: Number(first?.mapsClicks || 0),
            interactions: Number(first?.interactions || 0),
            interactionRate: Number(first?.interactionRate || 0),
          });
        }
      } catch { /* silently ignore mini stats errors */ }

    } catch (error) {
      console.error("Error:", error);
      toast({ title: "Σφάλμα", description: "Υπήρξε πρόβλημα κατά την ανάκτηση.", variant: "destructive" });
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [partnerId, timeFilter, toast]);

  useEffect(() => { fetchPartnerAndLeads(); }, [fetchPartnerAndLeads]);

  useEffect(() => {
    if (!partnerId || !autoRefresh) return;
    const interval = window.setInterval(() => fetchPartnerAndLeads(false), 30000);
    return () => window.clearInterval(interval);
  }, [partnerId, autoRefresh, fetchPartnerAndLeads]);

  const pipelineCounts = useMemo(() => ({
    new:     leads.filter((l) => l.stage === "Νέο").length,
    pending: leads.filter((l) => l.stage === "σεΕκκρεμότητα").length,
    info:    leads.filter((l) => l.stage === "ήθελεΠληροφορίες").length,
    closed:  leads.filter((l) => l.stage === "έκλεισεΗΔουλειά").length,
  }), [leads]);

  const topPriorityLeads = useMemo(() =>
    [...leads].sort((a, b) => calculateLeadScore(b) - calculateLeadScore(a)).slice(0, 5),
    [leads, calculateLeadScore]
  );

  const metrics = useMemo(() => {
    if (!leads.length) return null;
    const total = leads.length;
    const closed = leads.filter((l) => l.stage === "έκλεισεΗΔουλειά").length;
    const sent = leads.filter((l) => l.smsStatus === "sent" || l.smsStatus === "delivered").length;
    const viewed = leads.filter((l) => l.viewed).length;
    const now = new Date();
    const w = new Date(now); w.setDate(w.getDate() - 7);
    const m = new Date(now); m.setMonth(m.getMonth() - 1);
    return {
      total, closed,
      successRate: Math.round((closed / total) * 100),
      sent, viewed,
      viewRate: sent > 0 ? Math.round((viewed / sent) * 100) : 0,
      lastWeek: leads.filter((l) => new Date(l.createdAt) >= w).length,
      lastMonth: leads.filter((l) => new Date(l.createdAt) >= m).length,
    };
  }, [leads]);

  // ---------- Loading skeleton ----------
  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Skeleton className="h-52 w-full rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <Skeleton className="h-96 w-full rounded-xl" />
      </div>
    );
  }

  if (!partner) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
        <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
          <Users className="h-6 w-6 text-slate-400" />
        </div>
        <h2 className="text-xl font-bold text-slate-800 mb-1">Ο συνεργάτης δεν βρέθηκε</h2>
        <p className="text-slate-500 mb-6 text-sm">Δεν βρέθηκαν πληροφορίες για τον συγκεκριμένο συνεργάτη.</p>
        <Link to="/admin/partners">
          <Button variant="outline"><ArrowLeft className="mr-2 h-4 w-4" />Επιστροφή</Button>
        </Link>
      </div>
    );
  }

  const totalPipeline = pipelineCounts.new + pipelineCounts.pending + pipelineCounts.info + pipelineCounts.closed;
  const pct = (n: number) => totalPipeline > 0 ? Math.round((n / totalPipeline) * 100) : 0;

  return (
    <div className="space-y-6 animate-fade-in">

      {/* ── HERO CARD ── */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative">
          {/* decorative blurs */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-emerald-500/10 rounded-full blur-2xl" />
          </div>

          <div className="relative p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">

              {/* Left: avatar + info */}
              <div className="flex items-start gap-5">
                <Link to="/admin/partners">
                  <button className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors mt-1">
                    <ArrowLeft className="h-4 w-4 text-white/80" />
                  </button>
                </Link>

                {/* Initials avatar */}
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-2xl font-bold text-white">{getInitials(partner.name)}</span>
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <h1 className="text-2xl md:text-3xl font-bold text-white">{partner.name}</h1>
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs font-medium">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Ενεργός
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 text-sm text-white/60">
                    <a href={`tel:${partner.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                      <Phone className="h-3.5 w-3.5" />{partner.phone}
                    </a>
                    {partner.email && (
                      <a href={`mailto:${partner.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                        <Mail className="h-3.5 w-3.5" />{partner.email}
                      </a>
                    )}
                    <span className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" />{partner.location}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Calendar className="h-3.5 w-3.5" />
                      Μέλος από {format(new Date(partner.createdAt), "MMM yyyy", { locale: el })}
                    </span>
                  </div>

                  {/* Services */}
                  {partner.services?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {partner.services.map((s, i) => (
                        <span key={i} className="px-2.5 py-0.5 rounded-full bg-white/10 border border-white/20 text-white/80 text-xs">
                          {getServiceLabel(s)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right: controls */}
              <div className="flex flex-col gap-3 lg:items-end">
                {/* Live indicator */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setAutoRefresh((p) => !p)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      autoRefresh
                        ? "bg-emerald-500/20 border border-emerald-400/30 text-emerald-300"
                        : "bg-white/10 border border-white/20 text-white/60"
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${autoRefresh ? "bg-emerald-400 animate-pulse" : "bg-white/30"}`} />
                    Live {autoRefresh ? "ON" : "OFF"}
                  </button>
                  <button
                    onClick={() => fetchPartnerAndLeads(false)}
                    className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 border border-white/20 transition-colors"
                  >
                    <RefreshCw className="h-4 w-4 text-white/70" />
                  </button>
                </div>

                {/* Time filter */}
                <div className="flex items-center gap-1 bg-white/10 rounded-lg p-1">
                  {(Object.keys(TIME_FILTER_LABELS) as TimeFilter[]).map((f) => (
                    <button
                      key={f}
                      onClick={() => setTimeFilter(f)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                        timeFilter === f
                          ? "bg-white text-slate-900"
                          : "text-white/60 hover:text-white"
                      }`}
                    >
                      {TIME_FILTER_LABELS[f]}
                    </button>
                  ))}
                </div>

                {lastUpdated && (
                  <p className="text-xs text-white/40">
                    Ενημ. {format(lastUpdated, "HH:mm:ss", { locale: el })}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Inline KPI strip */}
          {metrics && (
            <div className="border-t border-white/10 grid grid-cols-2 md:grid-cols-4 divide-x divide-white/10">
              {[
                { label: "Σύνολο Leads",     value: metrics.total,             sub: `+${metrics.lastWeek} εβδομάδα`, icon: <Users className="h-4 w-4" /> },
                { label: "Κλεισμένες",        value: metrics.closed,            sub: `${metrics.successRate}% επιτυχία`, icon: <CheckCircle2 className="h-4 w-4" /> },
                { label: "SMS Προβολές",      value: `${metrics.viewRate}%`,    sub: `${metrics.viewed}/${metrics.sent} απεσταλμένα`, icon: <Eye className="h-4 w-4" /> },
                { label: "Τελ. 30 ημέρες",   value: metrics.lastMonth,         sub: "νέα leads", icon: <Activity className="h-4 w-4" /> },
              ].map(({ label, value, sub, icon }) => (
                <div key={label} className="px-6 py-4 flex items-center gap-3">
                  <div className="text-white/40">{icon}</div>
                  <div>
                    <p className="text-xl font-bold text-white">{value}</p>
                    <p className="text-xs text-white/50">{label}</p>
                    <p className="text-xs text-white/30">{sub}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {/* ── TABS ── */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-slate-100 p-1 rounded-xl h-auto">
          {[
            { value: "overview", label: "Επισκόπηση",      icon: <LayoutDashboard className="h-4 w-4" /> },
            { value: "sms",      label: "Μηνύματα SMS",    icon: <MessageSquare className="h-4 w-4" /> },
            { value: "email",    label: "Email",            icon: <Mail className="h-4 w-4" /> },
            { value: "leads",    label: "Leads",            icon: <User className="h-4 w-4" /> },
          ].map(({ value, label, icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              className="flex items-center gap-2 rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm px-4 py-2"
            >
              {icon}{label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ── OVERVIEW TAB ── */}
        <TabsContent value="overview" className="mt-6 space-y-6 animate-fade-in">

          {/* Mini Page Stats */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MonitorSmartphone className="h-4 w-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Mini Page Analytics</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Προβολές",       value: miniStats.views,                     gradient: "from-blue-500 to-indigo-600",    icon: <Globe className="h-4 w-4" /> },
                { label: "Κλήσεις",        value: miniStats.callClicks,                gradient: "from-emerald-500 to-teal-600",   icon: <Phone className="h-4 w-4" /> },
                { label: "Maps Clicks",    value: miniStats.mapsClicks,                gradient: "from-violet-500 to-purple-600",  icon: <MapPin className="h-4 w-4" /> },
                { label: "Αλληλεπίδραση", value: `${miniStats.interactionRate.toFixed(1)}%`, gradient: "from-amber-400 to-orange-500", icon: <Zap className="h-4 w-4" /> },
              ].map(({ label, value, gradient, icon }) => (
                <Card key={label} className={`bg-gradient-to-br ${gradient} border-transparent text-white overflow-hidden relative shadow-md`}>
                  <div className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                  <CardContent className="p-5 relative">
                    <div className="flex items-center justify-between mb-3">
                      <div className="p-1.5 rounded-lg bg-white/20">{icon}</div>
                    </div>
                    <p className="text-2xl font-bold">{value}</p>
                    <p className="text-xs text-white/70 mt-0.5">{label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Pipeline Funnel */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-slate-100">
                    <TrendingUp className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Pipeline Συνεργάτη</CardTitle>
                    <CardDescription>{totalPipeline} συνολικά leads στο pipeline</CardDescription>
                  </div>
                </div>
                <span className="text-2xl font-bold text-slate-900">{totalPipeline}</span>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { key: "new",     label: "Νέα",               count: pipelineCounts.new,     bar: "bg-violet-500",  text: "text-violet-700",  bg: "bg-violet-50",  border: "border-violet-100" },
                { key: "pending", label: "Σε Εκκρεμότητα",   count: pipelineCounts.pending,  bar: "bg-amber-500",   text: "text-amber-700",   bg: "bg-amber-50",   border: "border-amber-100"  },
                { key: "info",    label: "Πληροφορίες",       count: pipelineCounts.info,     bar: "bg-blue-500",    text: "text-blue-700",    bg: "bg-blue-50",    border: "border-blue-100"   },
                { key: "closed",  label: "Έκλεισε",           count: pipelineCounts.closed,   bar: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-100"},
              ].map(({ key, label, count, bar, text, bg, border }) => (
                <div key={key} className={`p-4 rounded-xl border ${bg} ${border}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-sm font-semibold ${text}`}>{label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-500">{pct(count)}%</span>
                      <span className={`text-xl font-bold ${text}`}>{count}</span>
                    </div>
                  </div>
                  <div className="h-2 bg-white/60 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${bar} rounded-full transition-all duration-700`}
                      style={{ width: `${pct(count)}%` }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Top Priority Leads */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-amber-50">
                  <Star className="h-4 w-4 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-lg">Top Προτεραιότητα</CardTitle>
                  <CardDescription>5 leads για άμεσο follow-up με βάση score</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {topPriorityLeads.length > 0 ? (
                <div className="space-y-3">
                  {topPriorityLeads.map((lead, idx) => {
                    const score = calculateLeadScore(lead);
                    const meta = getStageMeta(lead.stage);
                    const scoreColor = score >= 80 ? "bg-emerald-500" : score >= 60 ? "bg-amber-500" : "bg-slate-400";
                    const scoreText = score >= 80 ? "text-emerald-700" : score >= 60 ? "text-amber-700" : "text-slate-600";
                    return (
                      <div key={lead.id} className="flex items-center gap-4 p-4 rounded-xl border border-slate-100 bg-white hover:bg-slate-50 hover:border-slate-200 transition-all group">
                        {/* Rank */}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${idx === 0 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>
                          {idx + 1}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 text-sm truncate">{lead.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-400">{lead.phone}</span>
                            <span className="text-slate-200">•</span>
                            <span className="text-xs text-slate-400">{getServiceLabel(lead.service)}</span>
                          </div>
                          {/* Score bar */}
                          <div className="flex items-center gap-2 mt-1.5">
                            <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full ${scoreColor} rounded-full transition-all duration-700`} style={{ width: `${score}%` }} />
                            </div>
                            <span className={`text-xs font-semibold ${scoreText}`}>{score}</span>
                          </div>
                        </div>

                        {/* Stage + timing */}
                        <div className="flex flex-col items-end gap-1.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${meta.bg} ${meta.color}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                            {getStageLabel(lead.stage)}
                          </span>
                          <span className="text-xs text-slate-400 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {format(new Date(lead.createdAt), "dd/MM/yy")}
                          </span>
                        </div>

                        <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-slate-400 transition-colors flex-shrink-0" />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-10 text-slate-400">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Δεν υπάρχουν leads για προτεραιοποίηση.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Success Rate summary */}
          {metrics && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-blue-500 to-indigo-600 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <CardContent className="p-6 relative">
                  <Users className="h-5 w-5 text-white/60 mb-3" />
                  <p className="text-3xl font-bold">{metrics.total}</p>
                  <p className="text-sm text-white/70 mt-1">Σύνολο Leads</p>
                  <p className="text-xs text-white/50 mt-0.5">+{metrics.lastWeek} τελ. εβδομάδα</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-emerald-500 to-teal-600 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <CardContent className="p-6 relative">
                  <CheckCircle2 className="h-5 w-5 text-white/60 mb-3" />
                  <p className="text-3xl font-bold">{metrics.successRate}%</p>
                  <p className="text-sm text-white/70 mt-1">Ποσοστό Επιτυχίας</p>
                  <p className="text-xs text-white/50 mt-0.5">{metrics.closed} κλεισμένες συμφωνίες</p>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-violet-500 to-purple-600 text-white overflow-hidden relative">
                <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <CardContent className="p-6 relative">
                  <Eye className="h-5 w-5 text-white/60 mb-3" />
                  <p className="text-3xl font-bold">{metrics.viewRate}%</p>
                  <p className="text-sm text-white/70 mt-1">SMS Προβολή</p>
                  <p className="text-xs text-white/50 mt-0.5">{metrics.viewed} από {metrics.sent} SMS</p>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sms" className="mt-6 animate-fade-in">
          <PartnerSMSMetrics partnerId={partner.id} leads={leads} />
        </TabsContent>

        <TabsContent value="email" className="mt-6 animate-fade-in">
          <PartnerEmailMetrics partnerId={partner.id} partnerEmail={partner.email} leads={leads} />
        </TabsContent>

        <TabsContent value="leads" className="mt-6 animate-fade-in">
          <PartnerLeadsList partnerId={partner.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PartnerLeadsDashboard;
